import * as THREE from 'three';

import type { SimEvent, SimState } from '../core/sim.ts';
import { isEmbodied } from '../core/player.ts';

/**
 * Everything the match sounds like.
 *
 * Built on the same three rules the renderer arrived at the hard way, because
 * audio has exactly the same failure modes and they are less obvious when they
 * bite:
 *
 *   Nothing is created while playing. Voices are a pool, allocated once. An
 *   `AudioBufferSourceNode` is single-use by design, but the *panner* it feeds
 *   is not, and building a fresh panner per shot is how a spell exchange turns
 *   into a garbage-collection pause.
 *
 *   Everything is decoded behind the loading screen. Decoding a buffer is a
 *   synchronous stall on the audio thread the first time it happens, which is
 *   the same first-cast hitch the shaders had.
 *
 *   Nothing repeats exactly. A sound played twice with the same pitch reads as a
 *   machine gun; a few per cent of jitter is the difference between a game and a
 *   sample player.
 *
 * There is one thing here with no visual equivalent: the browser will not start
 * an `AudioContext` until the player has clicked something. That is not a bug to
 * work around — the loading screen already has a button, and this waits for it.
 */

/** What a sound is *for*, which decides how loud and how far it carries. */
export type SoundRole = 'launch' | 'impact' | 'whoosh' | 'hurt' | 'ui';

interface Cue {
  /** Files, without extension. More than one means variation. */
  takes: readonly string[];
  role: SoundRole;
  /** Loudness before distance, 0..1. */
  gain: number;
  /** Metres at which it can no longer be heard at all. */
  reach: number;
}

/**
 * The catalogue.
 *
 * Keyed by what happened rather than by spell, so a spell with no sound of its
 * own falls back to the sound of its kind. Forty-seven spells do not need
 * forty-seven recordings before any of them can be heard — they need one dart
 * launch and one dart impact, and the ones that deserve their own can have them
 * later without anything else changing.
 */
/**
 * What each spell sounds like, generated from the reference sheet.
 *
 * By spell rather than by kind, because a spell is where the decision actually
 * belongs: Stone Fist and Fire Bolt are the same *kind* and should not land the
 * same way. Several spells share a recording, which is the point — the sheet
 * assigns a generic launch to most things and a specific one where it matters,
 * so a new sound is a one-line change and nothing needs a recording before it
 * can be heard.
 *
 * Missing entries are silent rather than wrong. A spell with no sound is a spell
 * waiting for one, and a spell borrowing an unrelated recording is a spell that
 * has to be *un*-learned by whoever plays it.
 */
const SPELL_SOUNDS: Record<string, {
  launch?: string; impact?: string; whoosh?: string; loop?: string;
}> = {
  airwall: { launch: 'launch_whoosh' },
  bleeding: { launch: 'launch_major', whoosh: 'whoosh_generic' },
  blur: { launch: 'buff_futuristic' },
  burn: { launch: 'launch_dart', impact: 'impact_explode', whoosh: 'whoosh_fire', loop: 'loop_burning' },
  cold_ball: { launch: 'launch_generic', whoosh: 'whoosh_air' },
  cramp: { launch: 'launch_missile', whoosh: 'whoosh_generic' },
  dispell: { launch: 'launch_major' },
  electrocute: { launch: 'impact_zap', impact: 'impact_squish' },
  fire_ball: { launch: 'launch_generic', impact: 'impact_fire', whoosh: 'whoosh_fire', loop: 'loop_burning' },
  fire_bolt: { launch: 'launch_generic', impact: 'impact_fireburst', whoosh: 'whoosh_fire', loop: 'loop_burning' },
  fire_storm: { launch: 'launch_whoosh' },
  fracture: { launch: 'launch_missile', whoosh: 'whoosh_generic' },
  heal_other: { impact: 'heal_pop' },
  healing: { impact: 'heal_pop' },
  ice_dart: { launch: 'launch_dart', impact: 'impact_ice', whoosh: 'whoosh_air' },
  ice_storm: { launch: 'launch_whoosh' },
  jolts: { launch: 'impact_zap' },
  lightning_bolt: { launch: 'launch_shock', impact: 'impact_fireburst', whoosh: 'whoosh_electric' },
  mag_resist: { launch: 'buff_armor' },
  mana_ball: { launch: 'launch_generic', whoosh: 'whoosh_electric' },
  mana_bolt: { launch: 'launch_punchy', impact: 'impact_fireburst', whoosh: 'whoosh_electric' },
  mana_wall: { launch: 'conjure_wall' },
  manastrike: { launch: 'launch_dart', impact: 'impact_dart', whoosh: 'whoosh_electric' },
  ment_armor: { launch: 'buff_armor' },
  ment_resist: { launch: 'buff_armor' },
  pain: { launch: 'impact_zap', impact: 'impact_squish' },
  paralysis: { launch: 'launch_missile', whoosh: 'whoosh_generic' },
  prayer: { launch: 'buff_armor' },
  sharp_edges: { launch: 'launch_dart', impact: 'impact_physical', whoosh: 'whoosh_thud' },
  shock_bolt: { launch: 'launch_shock', impact: 'impact_fireburst', whoosh: 'whoosh_electric' },
  shock_dart: { launch: 'launch_dart', impact: 'impact_dart', whoosh: 'whoosh_electric' },
  shrapnel_ball: { launch: 'launch_generic', impact: 'impact_metal', whoosh: 'whoosh_thud' },
  sign_explode: { launch: 'launch_major' },
  sign_expulse: { launch: 'launch_major' },
  sign_hesitate: { launch: 'launch_major' },
  sign_unpower: { launch: 'launch_major' },
  soul_shock: { launch: 'launch_punchy', whoosh: 'whoosh_electric' },
  stone_fist: { launch: 'launch_generic', impact: 'impact_metal', whoosh: 'whoosh_thud' },
  target_resist: { launch: 'buff_futuristic' },
  void_ball: { launch: 'launch_generic', whoosh: 'whoosh_electric' },
  void_bolt: { launch: 'launch_shock', impact: 'impact_fireburst', whoosh: 'whoosh_electric' },
  void_spike: { launch: 'launch_dart', impact: 'impact_explode', whoosh: 'whoosh_electric' },
  wall_of_cold: { launch: 'conjure_wall' },
  wall_of_ice: { launch: 'conjure_wall' },
  wounding: { launch: 'launch_dart', impact: 'impact_physical', whoosh: 'whoosh_thud' },
  wounding_triad: { launch: 'launch_dart', impact: 'impact_physical', whoosh: 'whoosh_thud' },
};

/**
 * The wince, which is not a spell's sound but a body's.
 *
 * One recording whatever hit you, because being hurt is the message and what did
 * it is already being said by the impact playing at the same moment somewhere
 * in front of you.
 */
const HURT_TAKE = 'impact_squish';

/** How each role is mixed. */
const CUE_STYLE: Record<SoundRole, { gain: number; reach: number }> = {
  launch: { gain: 0.5, reach: 42 },
  impact: { gain: 0.65, reach: 48 },
  // Quiet and short-ranged on purpose: a pass-by is a warning about something
  // already very close, and one that carried across a nave would be constant.
  whoosh: { gain: 0.55, reach: 20 },
  hurt: { gain: 0.8, reach: 4 },
  ui: { gain: 0.7, reach: 4 },
};

/**
 * How many sounds may play at once, per role.
 *
 * Not a performance limit so much as a mix one. Eight simultaneous dart impacts
 * is not eight times as informative as one; it is a wall of noise with the
 * important sound somewhere inside it. When the budget is full the quietest
 * voice is taken, which in practice means the furthest away.
 */
const VOICES: Record<SoundRole, number> = {
  launch: 6, impact: 6, whoosh: 3, hurt: 2, ui: 2,
};

/** How loud a burning mage is, and how far it carries. */
const LOOP_GAIN = 0.4;
const LOOP_REACH = 22;

/** How much a sound's pitch wanders, either way. */
const PITCH_JITTER = 0.06;

/**
 * How near a projectile has to pass to be heard going by, and how long before
 * the same one may be heard again.
 *
 * A whoosh is the only cue not driven by an event: nothing in the simulation
 * says "a spell went past somebody". It has to be noticed, which means checking
 * every projectile against the listener every frame — cheap, and worth it,
 * because a shot you did not see going past is the single most useful thing
 * audio can tell a player.
 */
const WHOOSH_RANGE = 4.5;
const WHOOSH_ONCE = 0.6;

/** A sound that lasts as long as the thing making it. */
interface Loop {
  source: AudioBufferSourceNode;
  panner: PannerNode;
  gain: GainNode;
}

interface Voice {
  panner: PannerNode;
  gain: GainNode;
  /** When this voice becomes free again, in context time. */
  until: number;
  /** How loud it started, so the quietest can be stolen first. */
  level: number;
}

export class SoundStage {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private readonly buffers = new Map<string, AudioBuffer>();
  /** Downloaded but not yet decoded, because that needs a context. */
  private readonly encoded = new Map<string, ArrayBuffer>();
  private readonly voices: Record<SoundRole, Voice[]> = {
    launch: [], impact: [], whoosh: [], hurt: [], ui: [],
  };

  /**
   * Looping voices, one per mage with a condition that makes a noise.
   *
   * The one place a node is built outside `start`, and deliberately: a loop is
   * held for as long as a mage burns, which is seconds rather than a frame, and
   * there are at most as many as there are players. A pool would be a pool of
   * things that are almost always all in use.
   */
  private readonly running = new Map<string, Loop>();

  /** Projectiles already heard going past, and when. */
  private readonly whooshed = new Map<number, number>();
  private elapsed = 0;

  /**
   * Wakes the audio device. Must be called from a click or a key press.
   *
   * Browsers refuse to start an `AudioContext` any other way, and a context
   * created outside a gesture is not merely silent — it is suspended, and every
   * sound played into it queues up and arrives at once when it resumes.
   */
  async start(volume = 1): Promise<void> {
    if (this.ctx) return;
    const Ctor = window.AudioContext ?? (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;

    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = volume;
    this.master.connect(this.ctx.destination);

    for (const role of Object.keys(this.voices) as SoundRole[]) {
      for (let i = 0; i < VOICES[role]; i++) {
        const panner = this.ctx.createPanner();
        panner.panningModel = 'HRTF';
        panner.distanceModel = 'linear';
        panner.rolloffFactor = 1;
        const gain = this.ctx.createGain();
        gain.gain.value = 0;
        panner.connect(gain).connect(this.master);
        this.voices[role].push({ panner, gain, until: 0, level: 0 });
      }
    }
    await this.ctx.resume();
    // Decoding needs a context, and the context needs a click, so this cannot
    // happen on the loading screen however much one would like it to. What
    // *can* happen there is the download; see `prefetch`.
    await this.decode();
  }

  /**
   * Downloads every cue. Belongs on the loading screen; needs no context.
   *
   * Splitting this from the decode is not tidiness, it is the whole reason the
   * sound worked at all. The loading screen runs *before* the player clicks into
   * the arena, and a browser will not give you an `AudioContext` until they do —
   * so the original single `load()` found no context, returned quietly, and was
   * never called again. The match was silent for the rest of its life, with
   * nothing anywhere reporting a fault.
   *
   * Fetching does not care about any of that, and it is the slow half.
   */
  async prefetch(): Promise<void> {
    const ext = this.canPlayOpus() ? 'webm' : 'm4a';
    await Promise.all([...this.takes()].map(async (take) => {
      try {
        this.encoded.set(take, await (await fetch(`/audio/${take}.${ext}`)).arrayBuffer());
      } catch {
        // A missing sound is not worth failing a match over. It does not play;
        // everything else still does.
      }
    }));
  }

  /**
   * Turns the downloaded bytes into buffers. Needs a context, so it runs on the
   * click that starts one.
   *
   * Anything not prefetched is fetched here instead, so a stage that was never
   * given a loading screen still ends up with sound — a frame slower, once.
   */
  private async decode(): Promise<void> {
    if (!this.ctx) return;
    if (!this.encoded.size) await this.prefetch();

    await Promise.all([...this.encoded].map(async ([take, bytes]) => {
      try {
        this.buffers.set(take, await this.ctx!.decodeAudioData(bytes.slice(0)));
      } catch {
        // Ignore: an undecodable take is one sound, not a broken match.
      }
    }));
  }

  /** Every recording the catalogue names, once each. */
  private takes(): Set<string> {
    const wanted = new Set<string>();
    for (const spell of Object.values(SPELL_SOUNDS)) {
      /*
       * Every role, including the whoosh.
       *
       * The whoosh was left out when it was added, and nothing said so: a take
       * that is never prefetched is simply decoded the first time it is played,
       * which is a stall in the middle of a fight rather than an error anywhere.
       * Twenty-four recordings each stalled once — the first Wall of Cold, the
       * first wounding spell to hit a wall, the first fire spell to hit you.
       *
       * Written as a spread of the whole entry rather than a list of fields, so
       * a fourth role cannot be forgotten the same way.
       */
      for (const take of Object.values(spell)) {
        if (take) wanted.add(`spells/${take}`);
      }
    }
    wanted.add(`spells/${HURT_TAKE}`);
    return wanted;
  }

  /**
   * Opus in WebM is much smaller and every current browser plays it; the AAC
   * fallback exists for the ones that do not.
   */
  private canPlayOpus(): boolean {
    if (typeof document === 'undefined') return false;
    return document.createElement('audio')
      .canPlayType('audio/webm; codecs="opus"') !== '';
  }

  /** How many buffers are ready. Zero means the match will be silent. */
  get ready(): number {
    return this.buffers.size;
  }

  setVolume(volume: number): void {
    if (this.master) this.master.gain.value = volume;
  }

  /**
   * Moves the listener, and notices anything flying past it.
   *
   * The camera rather than the player, because what a player hears should match
   * what they are looking at — and in a first-person game those are the same
   * thing until somebody dies and becomes a ghost.
   */
  update(state: SimState, camera: THREE.Camera, localId: string | undefined, dt: number): void {
    if (!this.ctx) return;
    this.elapsed += dt;

    const at = camera.getWorldPosition(new THREE.Vector3());
    const facing = camera.getWorldDirection(new THREE.Vector3());
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion);
    const l = this.ctx.listener;

    if (l.positionX) {
      l.positionX.value = at.x;
      l.positionY.value = at.y;
      l.positionZ.value = at.z;
      l.forwardX.value = facing.x;
      l.forwardY.value = facing.y;
      l.forwardZ.value = facing.z;
      l.upX.value = up.x;
      l.upY.value = up.y;
      l.upZ.value = up.z;
    } else {
      // Safari, until recently.
      l.setPosition(at.x, at.y, at.z);
      l.setOrientation(facing.x, facing.y, facing.z, up.x, up.y, up.z);
    }

    this.whooshes(state, at, localId);
    this.loops(state);
  }

  /**
   * Sounds that last as long as a condition does.
   *
   * Everything else in here is an event: a thing happened, a sound plays, it
   * ends. Burning is not an event — it is four seconds of a mage being on fire,
   * and the only cue the game has for it is a number climbing on a bar. A
   * crackle that follows the burning mage around says it in a way a number
   * cannot, and says it to everyone near them rather than only to whoever is
   * looking at the right corner of the screen.
   *
   * One voice per burning mage, started when the effect appears and stopped when
   * it goes. Not restarted while it runs, or reapplying a burn — which a Magician
   * does constantly — would stutter the loop rather than extend it.
   */
  private loops(state: SimState): void {
    if (!this.ctx || !this.master) return;
    const alight = new Set<string>();

    for (const p of state.players) {
      if (!isEmbodied(p)) continue;
      const burning = p.effects.find((e) => SPELL_SOUNDS[e.spellId]?.loop);
      const take = burning ? SPELL_SOUNDS[burning.spellId]!.loop! : null;
      if (!take) continue;

      alight.add(p.id);
      let running = this.running.get(p.id);
      if (!running) {
        const started = this.startLoop(take);
        if (!started) continue;
        running = started;
        this.running.set(p.id, running);
      }
      running.panner.positionX.value = p.pos.x;
      running.panner.positionY.value = p.pos.y + 1;
      running.panner.positionZ.value = p.pos.z;
    }

    for (const [id, running] of this.running) {
      if (alight.has(id)) continue;
      running.source.stop();
      running.source.disconnect();
      this.running.delete(id);
    }
  }

  /** One looping voice, built where it is needed and torn down when it ends. */
  private startLoop(take: string): Loop | null {
    const buffer = this.buffers.get(`spells/${take}`);
    if (!buffer || !this.ctx || !this.master) return null;

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    // Started somewhere in the middle, so two mages alight at once are not one
    // sound at twice the volume.
    source.playbackRate.value = 1 + (Math.random() * 2 - 1) * PITCH_JITTER;

    const panner = this.ctx.createPanner();
    panner.panningModel = 'HRTF';
    panner.distanceModel = 'linear';
    panner.refDistance = 1.5;
    panner.maxDistance = LOOP_REACH;

    const gain = this.ctx.createGain();
    gain.gain.value = LOOP_GAIN;
    source.connect(panner).connect(gain).connect(this.master);
    source.start(0, Math.random() * buffer.duration);

    return { source, panner, gain };
  }

  /**
   * A spell going past your ear.
   *
   * Nothing in the simulation announces this, so it is noticed rather than
   * reported: every live projectile, every frame, against the listener. Once per
   * projectile — a spell that lingers in range must not sound like a swarm — and
   * never your own, because you already heard yourself cast it.
   */
  private whooshes(state: SimState, at: THREE.Vector3, localId: string | undefined): void {
    for (const pr of state.projectiles) {
      if (pr.ownerId === localId) continue;
      const last = this.whooshed.get(pr.id);
      if (last !== undefined && this.elapsed - last < WHOOSH_ONCE) continue;

      const near = Math.hypot(pr.pos.x - at.x, pr.pos.y - at.y, pr.pos.z - at.z);
      if (near > WHOOSH_RANGE) continue;

      this.whooshed.set(pr.id, this.elapsed);
      /*
       * Its own sound, at last.
       *
       * This played the spell's *launch* until now, for want of anything else —
       * which was audibly wrong: a Fire Bolt passing your ear sounded exactly
       * like one being cast behind you, so the one cue whose entire job is to
       * tell you where something is coming from was telling you the wrong thing.
       * A spell with no whoosh of its own is silent going past, which is honest.
       */
      this.play(this.cueFor('whoosh', pr.spellId), pr.pos, 0.55);
    }

    // Forget projectiles that are long gone, or the map grows all match.
    if (this.whooshed.size > 64) {
      for (const [id, when] of this.whooshed) {
        if (this.elapsed - when > 4) this.whooshed.delete(id);
      }
    }
  }

  /**
   * Turns a tick's events into sound.
   *
   * Driven by the same `SimEvent` list the HUD reads, which is what keeps audio
   * honest: a sound plays because something happened in the simulation, not
   * because the renderer thought something looked like it happened.
   */
  ingest(events: readonly SimEvent[], state: SimState, localId: string | undefined): void {
    if (!this.ctx) return;

    for (const ev of events) {
      switch (ev.type) {
        case 'cast': {
          const caster = state.players.find((p) => p.id === ev.playerId);
          if (caster) this.play(this.cueFor('launch', ev.spellId), caster.pos, 1);
          break;
        }

        case 'impact': {
          this.play(this.cueFor('impact', ev.spellId), ev.pos, 1);
          break;
        }

        case 'hit': {
          /*
           * Being hit is the one cue that is not positional.
           *
           * It is not a thing happening in the room, it is a thing happening to
           * you, and panning it would send the player looking for the source of
           * their own pain. Played flat and loud, at the listener.
           */
          if (ev.targetId !== localId) break;
          const me = state.players.find((p) => p.id === localId);
          // No spell id on a hit — it is the *being hit* that is being played,
          // and one wince serves whatever caused it.
          if (me && isEmbodied(me)) {
            this.play(
              { takes: [`spells/${HURT_TAKE}`], role: 'hurt', ...CUE_STYLE.hurt },
              me.pos, 1, true,
            );
          }
          break;
        }

        default:
          break;
      }
    }
  }

  /**
   * What this spell sounds like doing this, if anything.
   *
   * Silence is a real answer. The sheet marks some spells with no sound at all —
   * a wall has no impact, a protection has nothing to hit — and others are simply
   * waiting for a recording. Borrowing an unrelated one to fill the gap would
   * teach a player something they later have to unlearn.
   */
  private cueFor(event: 'launch' | 'impact' | 'whoosh', spellId: string): Cue | null {
    const take = SPELL_SOUNDS[spellId]?.[event];
    if (!take) return null;
    const style = CUE_STYLE[event];
    return { takes: [`spells/${take}`], role: event, gain: style.gain, reach: style.reach };
  }

  /**
   * Plays a cue at a place, taking a voice from the pool.
   *
   * The pitch wanders by a few per cent every time. Two identical shots sound
   * like a sample being retriggered; the same two a semitone apart sound like
   * two shots, and the whole difference is one multiplication.
   */
  private play(cue: Cue | null, at: { x: number; y: number; z: number }, level: number, flat = false): void {
    if (!cue || !this.ctx || !this.master) return;

    const take = cue.takes[Math.floor(Math.random() * cue.takes.length)]!;
    const buffer = this.buffers.get(take);
    if (!buffer) return;

    const voice = this.claim(cue.role, cue.gain * level);
    if (!voice) return;

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = 1 + (Math.random() * 2 - 1) * PITCH_JITTER;

    voice.panner.refDistance = flat ? 10_000 : 1.5;
    voice.panner.maxDistance = flat ? 10_000 : cue.reach;
    voice.panner.positionX.value = at.x;
    voice.panner.positionY.value = at.y;
    voice.panner.positionZ.value = at.z;

    voice.gain.gain.value = cue.gain * level;
    voice.level = cue.gain * level;
    voice.until = this.ctx.currentTime + buffer.duration / source.playbackRate.value;

    source.connect(voice.panner);
    source.start();
    // Single-use by design, so it is dropped when it finishes. The panner and
    // the gain it feeds are the expensive parts and those stay.
    source.onended = () => source.disconnect();
  }

  /**
   * A free voice, or the quietest one in use.
   *
   * Stealing by loudness rather than by age is what keeps the important sound
   * audible: the thing about to be cut off is the furthest away, which is the
   * one the player was least likely to be listening to.
   */
  private claim(role: SoundRole, level: number): Voice | null {
    const now = this.ctx!.currentTime;
    const pool = this.voices[role];

    let quietest: Voice | null = null;
    for (const voice of pool) {
      if (voice.until <= now) return voice;
      if (!quietest || voice.level < quietest.level) quietest = voice;
    }
    // Only steal from something quieter than what is asking.
    return quietest && quietest.level < level ? quietest : null;
  }

  dispose(): void {
    for (const running of this.running.values()) running.source.stop();
    this.running.clear();
    void this.ctx?.close();
    this.ctx = null;
    this.buffers.clear();
  }
}
